/**
 * "Your agreement" — everything this partner has actually agreed to, in one place.
 *
 * Four artefacts, shown apart because they carry different weight and a partner who cannot
 * tell them apart cannot tell what binds them:
 *
 *   1. AGREEMENT  — the executed contract, with their own particulars in it. **The only one
 *                   that binds**, and the only one the money gates read. It sits at the top
 *                   because when it is unsigned it is the one thing they have to act on.
 *   2. RATE CARD  — the pinned version, whole. The commercial substance.
 *   3. PROPOSAL   — the commercial conversation. NOT a contract. The accepted one names the
 *                   rate card version the partnership runs on.
 *   4. TERMS      — the house rules, ticked at registration. Same for everyone. Append-only:
 *                   a version bump adds a row, it does not overwrite the last one.
 *
 * Why a page at all: a partner who has to email someone to re-read their own rates does not
 * trust the rates. Every number here is read from the card they are pinned to, never typed.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ShieldCheck, Loader2, ArrowRight, AlertCircle } from 'lucide-react';
import AgreementSigner from '../../components/AgreementSigner';

const inr = (n) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');

const rungLabel = (rate) => {
  if (rate == null) return '—';
  if (typeof rate === 'object') return `${inr(rate.flat)} per client`;
  return `${Math.round(Number(rate) * 1000) / 10}%`;
};

const formatDate = (str) =>
  str ? new Date(str).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const PartnerAgreement = () => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () =>
    axios.get(`${API_URL}/api/partner/agreement`, { headers: getAuthHeaders() })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-[#64748B]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <p className="text-[#64748B]">Could not load your agreement. Try again in a moment.</p>
      </DashboardLayout>
    );
  }

  const card = data.rate_card || {};
  // A partner earns a DIFFERENT rate on each way a client can be engaged, at whatever tier
  // they are on. So the table is arrangement x tier, not one column of "your rate" — there
  // is no such single number any more.
  const ARRANGEMENTS = ['referral', 'affiliate', 'solution'];
  const commission = card.commission || {};
  const thresholds = card.tier_thresholds || {};
  const order = card.tier_order || ['silver', 'gold', 'platinum'];
  const wallet = card.wallet || {};
  const walletDiscount = wallet.discount || {};

  // The accepted proposal is the one that BOUND these rates. A declined or superseded one is
  // history, and showing it next to the live figures only raises the question of which counts.
  const proposals = data.proposals || [];
  const locked = proposals.find((p) => p.status === 'accepted');
  const open_ = locked ? null : proposals.find((p) => p.status === 'sent' || p.status === 'viewed');
  const acceptedTerms = (data.terms?.accepted || [])[0];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="partner-agreement">

        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Your agreement</h1>
          <p className="text-[#64748B] mt-1">
            What you agreed to, and the rates it runs on. Rate card v{card.version} is pinned to
            your partnership — if we publish a new card, yours does not move.
          </p>
        </div>

        {/* 1. The agreement. First on the page because when it is unsigned it is the one thing
            on here they have to act on, and everything below it is reference. */}
        <AgreementSigner data={data} onChanged={load} />

        {/* 2. The rates, under the proposal that locked them.
            These used to be two cards — a rate table, then a list of every proposal ever sent —
            which showed the same numbers twice and left a partner to work out which applied.
            The proposal they accepted is what SET these rates: it pinned the card version, and
            that pin is on their record for life. So it names the numbers, and the proposals
            that were superseded or declined are not part of it.

            It does not say "locked", and must not. A proposal binds nobody and says so on its
            own face — the agreement above is the instrument that binds (L57). Wording here
            that blurs the two is the exact confusion the three artefacts are kept apart to
            avoid. */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {locked
                ? `Your rates — from the proposal you accepted on ${formatDate(locked.decided_at)}`
                : `Your rates — rate card v${card.version}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm" data-testid="agreement-commission">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-[#64748B]">
                  <th className="text-left font-normal pb-2">Tier</th>
                  {ARRANGEMENTS.map((a) => (
                    <th key={a} className="text-left font-normal pb-2 capitalize">{a} client</th>
                  ))}
                  <th className="text-right font-normal pb-2">Reached at</th>
                </tr>
              </thead>
              <tbody>
                {order.map((tier) => (
                  <tr key={tier} className="border-t border-[#E2E2DF]">
                    <td className="py-3 capitalize font-medium text-[#1A1A1A]">{tier}</td>
                    {ARRANGEMENTS.map((a) => (
                      <td key={a} className="py-3 font-semibold text-[#1A1A1A]">
                        {rungLabel((commission[a] || {})[tier])}
                      </td>
                    ))}
                    <td className="py-3 text-right text-[#64748B]">
                      {thresholds[tier] != null
                        ? `${inr(thresholds[tier])} a month`
                        : 'where everyone starts'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {Object.keys(walletDiscount).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Wallet discount</CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm" data-testid="agreement-wallet">
                <thead>
                  <tr className="text-xs uppercase tracking-wide text-[#64748B]">
                    <th className="text-left font-normal pb-2">Tier</th>
                    <th className="text-right font-normal pb-2">Your discount</th>
                  </tr>
                </thead>
                <tbody>
                  {order.map((tier) => (
                    <tr key={tier} className="border-t border-[#E2E2DF]">
                      <td className="py-3 capitalize text-[#1A1A1A]">{tier}</td>
                      <td className="py-3 text-right font-semibold text-[#1A1A1A]">
                        {Math.round(Number(walletDiscount[tier] || 0) * 1000) / 10}% off
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-[#64748B] mt-4">
                Your discount follows your tier, whatever size you buy · minimum purchase{' '}
                {inr(wallet.floor)} · credits valid {wallet.expiry_months} months

              </p>
            </CardContent>
          </Card>
        )}

        {/* 3. The terms. The clickwrap. */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#64748B]" /> Partner terms
            </CardTitle>
          </CardHeader>
          <CardContent>
            {acceptedTerms ? (
              <div data-testid="agreement-terms">
                <p className="text-sm text-[#1A1A1A]">
                  You accepted <strong>v{acceptedTerms.terms_version}</strong> on{' '}
                  {formatDate(acceptedTerms.accepted_at)}.
                </p>
                {(data.terms.accepted || []).length > 1 && (
                  <ul className="mt-3 space-y-1">
                    {data.terms.accepted.slice(1).map((a, i) => (
                      <li key={i} className="text-xs text-[#64748B]">
                        Previously accepted v{a.terms_version} on {formatDate(a.accepted_at)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <p className="text-sm text-[#64748B]">No acceptance on record.</p>
            )}

            {data.terms?.needs_acceptance && (
              <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-900">
                  The terms have been updated to v{data.terms.current_version}. You'll be asked to
                  accept the new version next time you sign in.
                </p>
              </div>
            )}

            <Link to="/terms" className="inline-block mt-4">
              <Button size="sm" variant="outline">Read the terms</Button>
            </Link>
          </CardContent>
        </Card>

      </div>
    </DashboardLayout>
  );
};

export default PartnerAgreement;
